import React from "react";
import { Button, Input, message } from "antd";
import { CustomModal } from "../common/CustomModal";
import { checkPermission } from "../AdminUser/CheckAdminPermission";
import { FaqSectionBuilder, isVimeoUrl } from "./FaqSectionBuilder";
import { getFaqCategories } from "../../Graphs/generalFaq/getCategories";

const DEFAULT_CATEGORIES = [
  "Register & Login",
  "Corporate Account | Group Registration",
  "Payment",
  "Access",
  "Certificate",
  "Social Media Platform",
];

const SELECT_ARROW_BG = `url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2364748b%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E")`;


export class AddFaq extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      visible: false,
      question: "",
      category: DEFAULT_CATEGORIES[0],
      categoriesList: DEFAULT_CATEGORIES,
      customCategory: "",
      isCustomCategory: false,
      sections: [
        {
          description: "",
          image: "",
          video_url: "",
          order: 1,
        },
      ],
      isSubmitting: false,
    };
  }

  componentDidMount() {
    this.fetchCategories();
  }

  fetchCategories = async () => {
    try {
      const { authToken } = this.props;
      if (authToken) {
        const res = await getFaqCategories(authToken);
        if (res && res.status === 200 && Array.isArray(res.data)) {
          const extra = res.data.filter((c) => !DEFAULT_CATEGORIES.includes(c));
          this.setState({ categoriesList: [...DEFAULT_CATEGORIES, ...extra] });
        }
      }
    } catch (e) {
      console.log("Error loading categories", e);
    }
  };

  showModal = () => {
    this.setState({
      visible: true,
      question: "",
      category: DEFAULT_CATEGORIES[0],
      isCustomCategory: false,
      customCategory: "",
      sections: [
        {
          description: "",
          image: "",
          video_url: "",
          order: 1,
        },
      ],
    });
    this.fetchCategories();
  };

  handleCancel = () => {
    this.setState({ visible: false });
  };

  handleSectionsChange = (newSections) => {
    this.setState({ sections: newSections });
  };

  handleSubmit = async () => {
    const { question, category, isCustomCategory, customCategory, sections } =
      this.state;

    if (!question || !question.trim()) {
      message.error("Please enter a question");
      return;
    }

    const effectiveCategory = isCustomCategory
      ? customCategory.trim() || DEFAULT_CATEGORIES[0]
      : category || DEFAULT_CATEGORIES[0];

    // Validate sections
    const validSections = (sections || []).filter(
      (s) =>
        (s.description && s.description.trim()) ||
        s.image ||
        (s.video_url && s.video_url.trim())
    );

    if (validSections.length === 0) {
      message.error(
        "Please provide at least one section with Description, Image, or Vimeo Video"
      );
      return;
    }

    // Validate Vimeo URLs
    for (let i = 0; i < validSections.length; i++) {
      const sec = validSections[i];
      if (sec.video_url && !isVimeoUrl(sec.video_url)) {
        message.error(
          `Step ${i + 1}: Invalid Video URL. Only Vimeo URLs are allowed.`
        );
        return;
      }
    }

    // Calculate fallback answer string
    const fallbackAnswer = validSections
      .map((s, idx) => {
        const prefix = validSections.length > 1 ? `Step ${idx + 1}: ` : "";
        return s.description ? prefix + s.description : "";
      })
      .filter(Boolean)
      .join("\n\n");

    const payload = {
      question: question.trim(),
      category: effectiveCategory,
      answer: fallbackAnswer,
      sections: validSections,
    };

    this.setState({ isSubmitting: true });
    try {
      await this.props.onSubmit(payload);
      message.success("FAQ created successfully");
      this.setState({ visible: false });
    } catch (err) {
      console.log("Error submitting FAQ", err);
      message.error(typeof err === "string" ? err : "Failed to create FAQ");
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  render() {
    const { primaryColor, loginData } = this.props;
    const hasPermission = checkPermission(loginData, "general_faq", "create");
    const {
      visible,
      question,
      category,
      categoriesList,
      isCustomCategory,
      customCategory,
      sections,
      isSubmitting,
    } = this.state;

    return (
      <div>
        <div
          onClick={hasPermission ? this.showModal : null}
          style={{
            width: "100px",
            backgroundColor: primaryColor,
            color: "white",
            padding: "7px 0px",
            textAlign: "center",
            borderRadius: "50px",
            cursor: hasPermission ? "pointer" : "not-allowed",
            opacity: hasPermission ? 1 : 0.5,
            pointerEvents: hasPermission ? "auto" : "none",
          }}
        >
          ADD
        </div>

        <CustomModal
          destroyOnClose={true}
          title="Add New FAQ"
          visible={visible}
          onCancel={this.handleCancel}
          width="850px"
          bodyStyle={{ padding: 0 }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              maxHeight: "80vh",
              height: "100%",
            }}
          >
            {/* Scrollable Form Content */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "20px 24px",
              }}
            >
              {/* Category (Native Built-in OS Menu) */}
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontWeight: 600,
                    color: "#334155",
                    marginBottom: "6px",
                  }}
                >
                  Category
                </label>
                <div style={{ display: "flex", gap: "10px" }}>
                  {!isCustomCategory ? (
                    <select
                      value={category}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "__custom__") {
                          this.setState({
                            isCustomCategory: true,
                            customCategory: "",
                          });
                        } else {
                          this.setState({ category: val });
                        }
                      }}
                      style={{
                        flex: 1,
                        height: "38px",
                        padding: "8px 36px 8px 14px",
                        borderRadius: "6px",
                        border: "1px solid #d9d9d9",
                        backgroundColor: "#fff",
                        fontSize: "14px",
                        outline: "none",
                        cursor: "pointer",
                        color: "#1e293b",
                        appearance: "none",
                        WebkitAppearance: "none",
                        MozAppearance: "none",
                        backgroundImage: SELECT_ARROW_BG,
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "calc(100% - 14px) center",
                        backgroundSize: "14px",
                      }}
                    >
                      {categoriesList.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                      <option
                        value="__custom__"
                        style={{ color: "#1890ff", fontWeight: "bold" }}
                      >
                        + Add New Category...
                      </option>
                    </select>
                  ) : (
                    <div style={{ display: "flex", gap: "8px", flex: 1 }}>
                      <Input
                        placeholder="Type custom category name..."
                        value={customCategory}
                        onChange={(e) =>
                          this.setState({ customCategory: e.target.value })
                        }
                        style={{ flex: 1, borderRadius: "6px" }}
                      />
                      <Button
                        onClick={() =>
                          this.setState({ isCustomCategory: false })
                        }
                      >
                        Choose from List
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Question */}
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontWeight: 600,
                    color: "#334155",
                    marginBottom: "6px",
                  }}
                >
                  Question <span style={{ color: "red" }}>*</span>
                </label>
                <Input
                  placeholder="Enter FAQ Question (e.g. How can I purchase courses in bulk?)"
                  value={question}
                  onChange={(e) => this.setState({ question: e.target.value })}
                  style={{ borderRadius: "6px" }}
                />
              </div>

              {/* Sections / Steps Builder */}
              <FaqSectionBuilder
                sections={sections}
                onChange={this.handleSectionsChange}
              />
            </div>

            {/* Pinned Bottom Buttons */}
            <div
              style={{
                position: "sticky",
                bottom: 0,
                background: "#ffffff",
                padding: "14px 24px",
                borderTop: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.05)",
                zIndex: 10,
              }}
            >
              <Button onClick={this.handleCancel} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={this.handleSubmit}
                loading={isSubmitting}
                style={{
                  background: "#5C6AC4",
                  borderColor: "#5C6AC4",
                  color: "#fff",
                  borderRadius: "6px",
                  padding: "0 24px",
                }}
              >
                Create FAQ
              </Button>
            </div>
          </div>
        </CustomModal>
      </div>
    );
  }
}
